import {useState, useEffect, useRef, useMemo, useCallback} from 'react'
import {useForm, useFieldArray} from 'react-hook-form'
import {useAppState} from '../../../state'
import {CoolerCycleType, CoolerType} from '../../../state/rest'
import {localTempValue} from '../../../config/utils'
import {Site} from '../../../state/state'
import {v4 as uuid} from 'uuid'
import {useTranslation} from 'react-i18next'

type CoolerTypeFormRow = {
  coolerTypeId: string
  cycleType: CoolerCycleType
  inUse?: boolean
  markedForDelete?: boolean
  fastCycleTargetValue?: number
  fastCycleStartValue?: number
  fastCycleTimeLimit?: number
  slowCycleTargetValue?: number
  slowCycleStartValue?: number
  slowCycleTimeLimit?: number
}

type FormShape = {coolerTypes: CoolerTypeFormRow[]}

export function useCoolerTypeList() {
  const {state, actions, effects} = useAppState()
  const [removeIds, setRemoveIds] = useState<string[]>([])
  const initialRowsRef = useRef<CoolerTypeFormRow[]>([])

  const {
    watch,
    handleSubmit,
    errors,
    control,
    formState,
    triggerValidation,
    reset,
    setValue,
    getValues,
    register,
    unregister
  } = useForm<FormShape>({
    defaultValues: {coolerTypes: []},
    mode: 'onChange'
  })

  const {fields, append, remove} = useFieldArray({control, name: 'coolerTypes'})

  const valueExists = (v?: number) => !(v === undefined || v === null)

  const isRemoved = (id?: string) => !!id && removeIds.includes(id)

  const computeInUseIds = (): string[] => {
    const chain = state.v1.appliances.chainWideAppliances.data
    const list = chain?.appliancesForChainByCategory?.cooler ?? []
    return list.map((e: any) => e?.equipmentType?.id).filter(Boolean) as string[]
  }

  const toFormRow = (type: CoolerType, inUseIds: string[], site?: Site | null): CoolerTypeFormRow => ({
    coolerTypeId: type.id,
    cycleType: type.cycleType,
    inUse: inUseIds.includes(type.id),
    markedForDelete: false,
    fastCycleTargetValue: valueExists(type.fastCycleTargetValue)
      ? localTempValue(type.fastCycleTargetValue!, site!)
      : undefined,
    fastCycleStartValue: valueExists(type.fastCycleStartValue)
      ? localTempValue(type.fastCycleStartValue!, site!)
      : undefined,
    fastCycleTimeLimit: valueExists(type.fastCycleTimeLimit)
      ? localTempValue(type.fastCycleTimeLimit!, site!)
      : undefined,
    slowCycleTargetValue: valueExists(type.slowCycleTargetValue)
      ? localTempValue(type.slowCycleTargetValue!, site!)
      : undefined,
    slowCycleStartValue: valueExists(type.slowCycleStartValue)
      ? localTempValue(type.slowCycleStartValue!, site!)
      : undefined,
    slowCycleTimeLimit: valueExists(type.slowCycleTimeLimit)
      ? localTempValue(type.slowCycleTimeLimit!, site!)
      : undefined
  })

  const stripForCompare = (r: CoolerTypeFormRow) => ({
    coolerTypeId: r.coolerTypeId,
    cycleType: r.cycleType,
    fastCycleTargetValue: r.fastCycleTargetValue,
    fastCycleStartValue: r.fastCycleStartValue,
    fastCycleTimeLimit: r.fastCycleTimeLimit,
    slowCycleTargetValue: r.slowCycleTargetValue,
    slowCycleStartValue: r.slowCycleStartValue,
    slowCycleTimeLimit: r.slowCycleTimeLimit
  })

  const rowsChanged = (prev: CoolerTypeFormRow, curr: CoolerTypeFormRow) => {
    const A = stripForCompare(prev)
    const B = stripForCompare(curr)
    for (const k in A) {
      if (A[k as keyof typeof A] !== B[k as keyof typeof B]) return true
    }
    return false
  }

  const isNew = (id: unknown) => String(id).startsWith('new_')

  const parseNumber = (raw: any): number | undefined => {
    const v = typeof raw === 'string' ? raw.trim() : raw
    if (v === '' || v === null || v === undefined) return undefined
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }

  const path = (idx: number, key: keyof CoolerTypeFormRow) => `coolerTypes.${idx}.${key}`

  const getCell = <K extends keyof CoolerTypeFormRow>(idx: number, key: K): CoolerTypeFormRow[K] | undefined => {
    const form = getValues({nest: true}) as {coolerTypes?: CoolerTypeFormRow[]}
    const rows = form.coolerTypes ?? []
    return rows[idx]?.[key]
  }

  const setCell = <K extends keyof CoolerTypeFormRow>(idx: number, key: K, value: CoolerTypeFormRow[K]) => {
    setValue(path(idx, key), value as any, {shouldDirty: true, shouldValidate: true} as any)
    const form = getValues({nest: true}) as {coolerTypes?: CoolerTypeFormRow[]}
    const rows = form.coolerTypes ?? []
  }

  const makeNumberChange = (idx: number, key: keyof CoolerTypeFormRow) => (
    e: React.ChangeEvent<HTMLInputElement> | number | string
  ) => {
    const raw = typeof e === 'object' && 'target' in e ? e.target.value : e
    setCell(idx, key, parseNumber(raw) as any)
  }

  const makeBlur = (idx: number, key: keyof CoolerTypeFormRow) => () => triggerValidation?.(`coolerTypes.${idx}.${key}`)

  const makeTextChange = (idx: number, key: keyof CoolerTypeFormRow) => (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const raw = typeof e === 'object' && 'target' in e ? e.target.value : e
    setCell(idx, key, (raw as any) ?? '')
  }

  useEffect(() => {
    const inUseIds = computeInUseIds()
    const hasData = Array.isArray(state.coolerTypes) && state.coolerTypes.length > 0
    if (!hasData || !state.site) return

    const next = state.coolerTypes.map(ct => toFormRow(ct, inUseIds, state.site))
    reset({coolerTypes: next})
    initialRowsRef.current = next.map(r => ({...stripForCompare(r)}))
  }, [state.coolerTypes, state.site, state.v1.appliances.chainWideAppliances.loading])

  const createCoolerType = async (t: CoolerTypeFormRow): Promise<void> => {
    await effects.equipmentApi.createCoolerType(stripForCompare(t))
  }

  const updateCoolerType = async (t: CoolerTypeFormRow): Promise<void> => {
    await effects.equipmentApi.updateCoolerType({id: t.coolerTypeId, ...stripForCompare(t)})
  }

  const removeCoolerType = async (id: string): Promise<void> => {
    await effects.equipmentApi.deleteCoolerType(id)
  }

  const saveCoolerTypes = async (): Promise<void> => {
    const form = getValues({nest: true}) as {coolerTypes?: CoolerTypeFormRow[]}
    const rows = form.coolerTypes ?? []
    const creates = rows.filter(r => isNew(r.coolerTypeId))
    const updates = rows.filter(r => !isNew(r.coolerTypeId))
    const updateChanged = updates.filter(r => {
      const prevIdx = initialRowsRef.current.findIndex(p => p.coolerTypeId === r.coolerTypeId)
      return prevIdx >= 0 ? rowsChanged(initialRowsRef.current[prevIdx], r) : true
    })

    const deleteOps = removeIds.map(async id => {
      try {
        if (!isNew(id)) await removeCoolerType(id)
      } catch (ex) {
        // re-mark inUse if still present
        const idx = rows.findIndex(ct => ct.coolerTypeId === id)
        if (idx >= 0) setValue(`coolerTypes.${idx}.inUse`, true)
        throw ex
      }
    })

    await Promise.all([...creates.map(createCoolerType), ...updateChanged.map(updateCoolerType), ...deleteOps])

    setRemoveIds([])
    await actions.getCoolerTypes()
    // refresh snapshot
    const latestForm = getValues({nest: true}) as {coolerTypes?: CoolerTypeFormRow[]}
    const latest = latestForm.coolerTypes ?? []
    initialRowsRef.current = latest.map(r => ({...stripForCompare(r)}))
  }

  const onSubmit = handleSubmit(async () => {
    await saveCoolerTypes()
  })

  const handleRemove = (index: number) => {
    setValue(`coolerTypes.${index}.markedForDelete`, true, {shouldDirty: true} as any)
    const form = getValues({nest: true}) as {coolerTypes?: CoolerTypeFormRow[]}
    const row = form.coolerTypes?.[index]

    if (!row) return

    if (row.inUse) return

    setRemoveIds(prev => [...prev, String(row.coolerTypeId)])
  }

  const handleAdd = () => {
    append({
      coolerTypeId: `new_${uuid()}`,
      inUse: false,
      markedForDelete: false,
      cycleType: CoolerCycleType.SLOW,
      fastCycleTargetValue: undefined,
      fastCycleStartValue: undefined,
      fastCycleTimeLimit: undefined,
      slowCycleTargetValue: 6,
      slowCycleStartValue: 60,
      slowCycleTimeLimit: 240
    })
    triggerValidation?.('coolerTypes')
  }

  return {
    fields,
    errors,
    formState,
    getCell,
    makeNumberChange,
    makeTextChange,
    makeBlur,
    handleAdd,
    isRemoved,
    handleRemove,
    onSubmit,
    register
  }
}

function optionalLocalTempValue(selectedSite: Site, value?: number): number | undefined {
  if (value === undefined || value === null) return undefined
  return localTempValue(value, selectedSite)
}

type CoolerTypeOption = {id: string; option: string}

export function useCoolerTypeSelect(selectedSite: Site, saved?: Partial<CoolerType>, defaultOption?: string) {
  const {state} = useAppState()
  const {t} = useTranslation('appliances')

  const coolerTypeOptions: CoolerTypeOption[] = useMemo(() => {
    const name = t('appliances:equipmentTypes.cooler', 'Cooler')
    return (state.coolerTypes ?? []).map((o: CoolerType) => {
      const fastTime = optionalLocalTempValue(selectedSite, o.fastCycleTimeLimit)
      const slowTime = optionalLocalTempValue(selectedSite, o.slowCycleTimeLimit)

      let ranges: string[] = []
      switch (o.cycleType) {
        case 'SLOW':
          if (slowTime) ranges.push(`${slowTime} min`)
          break
        case 'FAST':
          if (fastTime) ranges.push(`${fastTime} min`)
          break
        case 'SLOW-FAST':
          if (slowTime) ranges.push(`${slowTime} min`)
          if (fastTime) ranges.push(`${fastTime} min`)
          break
        default:
          // ignore unsupported types; do not crash UI
          break
      }
      const rangeInfo = ranges.length > 0 ? ` (${ranges.join(', ')})` : ''
      return {id: o.id, option: `${name}${rangeInfo}`}
    })
  }, [state.coolerTypes, selectedSite, t])

  const byId = state.coolerTypeById as Record<string, CoolerType | undefined>

  const initialSelected: CoolerType | undefined = useMemo(() => {
    if (saved?.id && byId[saved.id]) return byId[saved.id]
    if (defaultOption && byId[defaultOption]) return byId[defaultOption]
    return state.coolerTypes?.[0]
  }, [saved?.id, defaultOption, byId, state.coolerTypes])

  const [selected, setSelected] = useState<Partial<CoolerType> | undefined>(initialSelected)

  useEffect(() => {
    if (!selected) {
      setSelected(initialSelected)
      return
    }
    const currentId = (selected as CoolerType)?.id
    if (!currentId) {
      setSelected(initialSelected)
      return
    }
    if (!byId[currentId]) {
      setSelected(initialSelected)
    }
  }, [byId, selected, initialSelected])

  const selectCoolerType = useCallback(
    (id: string) => {
      const ct = byId[id]
      if (ct) setSelected(ct)
    },
    [byId]
  )

  return {
    selectedCoolerType: selected,
    selectCoolerType,
    coolerTypeOptions
  }
}
